import type { WebSocketChannel } from "@huub-web/backend";

// ─── Backend DTOs ───────────────────────────────────────────

export type RoomCondition = "shell" | "dated" | "good";

/**
 * Classification data from the backend's property analysis pipeline.
 * Populated during "woning open" — no separate assessment call needed.
 */
export type Classification = {
  room_type: string | null;
  room_condition: RoomCondition | null;
  furnished: boolean | null;
  inside_or_outside: "in" | "out" | null;
};

/**
 * Flat action shape the backend POST /generate endpoint expects.
 * One action per target_panel — frontend composite actions
 * are split by toBackendActions() before sending.
 *
 * Union mirrors backend TARGET_PANELS. "furnish" is valid on the
 * backend but unused by toBackendActions — FurnishingAction maps
 * to "style" + "room_type" instead (each has its own chip category).
 */
export type BackendGenerationAction = {
  target_panel:
    | "floor"
    | "wall"
    | "wall_color"
    | "style"
    | "room_type"
    | "furnish"
    | "empty_room"
    | "free_wish";
  chip_id: string | null;
  free_text: string;
};

/**
 * Photo shape from the backend WS `ready` event.
 * Field differences from frontend Photo:
 * - `created` / `updated` instead of `created_at`
 * - classification fields are flat (not nested)
 * - has `walter_address_object_id` and `project_id` (dropped)
 */
export type BackendPhoto = {
  id: string;
  origin: "funda" | "generated" | "upload";
  blob_url: string | null;
  thumbnail_url: string | null;
  parent_photo_id: string | null;
  actions: unknown;
  room_type: string | null;
  room_condition: RoomCondition | null;
  furnished: boolean | null;
  inside_or_outside: "in" | "out" | null;
  deleted_at: string | null;
  created: string;
  updated: string;
};

/**
 * A node in the photo generation tree.
 * Backend returns flat array with parent_photo_id references.
 */
export type TreeNode = {
  id: string;
  parent_photo_id: string | null;
  origin: "funda" | "generated" | "upload";
  thumbnail_url: string;
  actions: GenerationAction[] | null;
  classification: Classification;
  created_at: string;
  deleted_at: string | null;
};

/**
 * Full Photo object from GET /photo/{id} and WS `ready` event.
 * Includes blob_url for full-resolution canvas display.
 */
export type Photo = {
  id: string;
  origin: "funda" | "generated" | "upload";
  blob_url: string;
  thumbnail_url: string;
  parent_photo_id: string | null;
  actions: GenerationAction[] | null;
  classification: Classification;
  deleted_at: string | null;
  created_at: string;
};

/**
 * Inspecteur observation with structured prefill for panels.
 * Field names match backend contract (03-frontend-api-data-model.md §2.4).
 */
export type PhotoSuggestion = {
  id: string;
  target_panel:
    | "binnenafwerking"
    | "inrichten"
    | "muurkleur"
    | "overige_wensen";
  structured_prefill: Record<string, string>;
  free_text_prefill: string | null;
  label: string;
  rationale: string;
  source_observation: {
    id: string;
    category_name: string;
    onderdeel: string;
    toelichting: string;
  };
};

// ─── Generation Actions ─────────────────────────────────────

/**
 * Field names match backend contract exactly (03-frontend-api-data-model.md §2.3).
 * NL field names (extra_wensen) are intentional — they match the wire format.
 * Fields are optional with at-least-one validators on the backend.
 */
export type InteriorFinishingAction = {
  type: "interior_finishing";
  floor?: string;
  wall?: string;
  extra_wensen?: string;
};

export type FurnishingAction = {
  type: "furnishing";
  style?: string;
  room_type?: string;
  extra_wensen?: string;
};

export type WallColorAction = {
  type: "wall_color";
  ral_code?: string;
  custom_text?: string;
  scope: "all";
  extra_wensen?: string;
};

export type RemoveFurnitureAction = {
  type: "remove_furniture";
};

export type OtherWishesAction = {
  type: "other_wishes";
  text: string;
};

export type DayNightAction = {
  type: "day_night";
  mode: "day" | "night";
};

export type GenerationAction =
  | InteriorFinishingAction
  | FurnishingAction
  | WallColorAction
  | RemoveFurnitureAction
  | OtherWishesAction
  | DayNightAction;

// ─── WebSocket Event Types ─────────────────────────────────

/**
 * Discriminated union for WS events on the
 * INTERIOR_DESIGNER_GENERATION channel.
 * Local to InteriorDesigner — not in packages/backend.
 */
export type InteriorDesignerWsEventData =
  | {
      status: "started";
      generation_id: string;
      started_at: string;
      estimated_seconds: number;
    }
  | {
      status: "pending";
      generation_id: string;
      elapsed_seconds: number;
      progress_message: string;
    }
  | {
      status: "ready";
      generation_id: string;
      photo: BackendPhoto;
    }
  | {
      status: "failed";
      generation_id: string;
      error_code: string;
      error_message: string;
    };

/**
 * Full WS message envelope for interior designer generation events.
 */
export type InteriorDesignerWsMessage = {
  type: WebSocketChannel.INTERIOR_DESIGNER_GENERATION;
  data: InteriorDesignerWsEventData;
};

// ─── Panel + UI State ───────────────────────────────────────

export type PanelId =
  | "interiorFinishing"
  | "furnishing"
  | "wallColor"
  | "otherWishes"
  | "history";

export type GenerationStatus = "idle" | "loading" | "error";

/**
 * Normalizes Dutch target_panel values from backend to frontend PanelId.
 */
export const TARGET_PANEL_TO_PANEL_ID: Record<
  PhotoSuggestion["target_panel"],
  PanelId
> = {
  binnenafwerking: "interiorFinishing",
  inrichten: "furnishing",
  muurkleur: "wallColor",
  overige_wensen: "otherWishes",
};

/**
 * Vocab entry shape — matches backend GET /vocab response items.
 */
export type VocabEntry = {
  id: string;
  label_nl: string;
};

/**
 * Extends VocabEntry with a hex color for swatch preview.
 * Matches the RAL color entries from the backend vocab endpoint.
 */
export type RalColorEntry = VocabEntry & {
  hex: string;
};

/**
 * Raw JSON shape from GET /interior-designer/vocab endpoint.
 * All vocab categories use VocabEntry (id + label_nl).
 */
export type VocabResponse = {
  version: string;
  floors: VocabEntry[];
  walls: VocabEntry[];
  styles: VocabEntry[];
  room_types: VocabEntry[];
  ral_colors: VocabEntry[];
};

/**
 * Frontend-enriched vocab where ral_colors gains hex field via RalColorEntry.
 * Used by useVocab hook after backend fetch + enrichment transformation.
 */
export type EnrichedVocab = Omit<VocabResponse, "ral_colors"> & {
  ral_colors: RalColorEntry[];
};

// ─── State Shape ────────────────────────────────────────────

/**
 * Per-panel selection state.
 *
 * Field names match the backend contract (`03-frontend-api-data-model.md`
 * §2.3 + §2.4 `structured_prefill`) — no translation layer between the
 * reducer and `POST /generate` action payloads. The `SUGGESTION_APPLIED`
 * reducer branch spreads `structured_prefill` directly onto these objects.
 */
export type PanelSelections = {
  interiorFinishing: {
    floor: string | null;
    wall: string | null;
    extra_wensen: string;
  };
  furnishing: {
    style: string | null;
    room_type: string;
    extra_wensen: string;
  };
  wallColor: {
    ral_code: string | null;
    custom_text: string;
    extra_wensen: string;
  };
  otherWishes: {
    text: string;
  };
};

export type InteriorDesignerState = {
  /**
   * Funda listing photo — tree root (immutable after init)
   */
  rootPhotoId: string;
  /**
   * Currently displayed tree node ID
   */
  activeNodeId: string;
  /**
   * Flat array of all nodes in the photo generation tree
   */
  tree: TreeNode[];
  /**
   * Inspecteur observations for the Aanbevelingen panel
   */
  suggestions: PhotoSuggestion[];

  /**
   * Full Photo from GET /photo/{id} — includes blob_url.
   * null before initial load; kept during navigation so the
   * Canvas can show the previous photo while the new one loads.
   */
  activePhoto: {
    blob_url: string;
    classification: Classification;
  } | null;

  /**
   * True while fetching a photo after history-panel navigation.
   * Canvas keeps showing the previous photo with a loading overlay.
   */
  isNavigating: boolean;

  /**
   * Stores the previous activeNodeId during navigation so
   * NAVIGATION_FAILED can revert to it.
   */
  navigatingFromNodeId: string | null;

  /**
   * Shorthand for activePhoto.classification (kept for selector ergonomics)
   */
  classification: Classification;

  /**
   * AI generation pipeline state
   */
  generation: {
    status: GenerationStatus;
    generationId: string | null;
    actions: GenerationAction[] | null;
    progressMessage: string | null;
    elapsedSeconds: number | null;
    errorCode: string | null;
    /**
     * Captured at GENERATION_REQUESTED time so GENERATION_FAILED
     * records the correct parent even if the user navigated away.
     */
    parentPhotoId: string | null;
    lastFailedRequest: {
      actions: GenerationAction[];
      parentPhotoId: string;
    } | null;
  };

  /**
   * Currently open panel (null = no panel open)
   */
  activePanel: PanelId | null;
  /**
   * Per-panel form selections
   */
  panelSelections: PanelSelections;

  /**
   * Whether furniture is currently visible (toggle state)
   */
  furnitureVisible: boolean;
  /**
   * WebSocket disconnected
   */
  isOffline: boolean;

  /**
   * Progression flags — drive chip availability.
   * Stored (not derived) because unlocks are permanent within a session.
   */
  hasCompletedFinishing: boolean;
  hasCompletedFurnishing: boolean;
};

// ─── Actions ────────────────────────────────────────────────

export type InteriorDesignerAction =
  | {
      type: "PHOTO_LOADED";
      photo: Photo;
      tree: TreeNode[];
      suggestions: PhotoSuggestion[];
    }
  | { type: "TREE_REFRESHED"; tree: TreeNode[] }
  | { type: "PANEL_OPENED"; panel: PanelId }
  | { type: "PANEL_CLOSED" }
  | {
      type: "SELECTION_CHANGED";
      panel: PanelId;
      field: string;
      value: unknown;
    }
  | {
      type: "SUGGESTION_APPLIED";
      panel: PanelId;
      prefill: Record<string, string>;
      freeText: string | null;
    }
  | { type: "GENERATION_STARTED"; generationId: string }
  | { type: "GENERATION_REQUESTED"; actions: GenerationAction[] }
  | {
      type: "GENERATION_PROGRESS";
      generationId: string;
      message: string;
      elapsedSeconds: number;
    }
  | { type: "GENERATION_SUCCEEDED"; generationId: string; photo: Photo }
  | {
      type: "GENERATION_FAILED";
      generationId: string | null;
      errorMessage: string;
      errorCode: string;
    }
  | { type: "GENERATION_RETRIED" }
  | { type: "GENERATION_DISMISSED" }
  | { type: "CONNECTIVITY_LOST" }
  | { type: "CONNECTIVITY_RESTORED" }
  | { type: "FURNITURE_TOGGLED" }
  | { type: "NODE_NAVIGATED"; nodeId: string }
  | {
      type: "ACTIVE_PHOTO_FETCHED";
      blobUrl: string;
      classification: Classification;
    }
  | { type: "NAVIGATION_FAILED" }
  | {
      type: "NODE_SOFT_DELETED";
      nodeId: string;
      fallsBackTo: string | null;
      deletedAt: string;
    };
